import api from "./api";

const DEFAULT_DOWNLOAD_ERROR = "Unable to download invoice.";

const getBackendBaseUrl = () => {
  const fallbackOrigin =
    typeof window !== "undefined" ? window.location.origin : "";
  const apiBaseUrl =
    process.env.REACT_APP_API_BASE_URL || fallbackOrigin;

  return apiBaseUrl.replace(/\/api\/?$/, "") || fallbackOrigin;
};

const normalizeResponseData = async (data) => {
  if (!data) {
    return null;
  }

  if (typeof Blob !== "undefined" && data instanceof Blob) {
    const text = await data.text();

    if (!text) {
      return null;
    }

    try {
      return JSON.parse(text);
    } catch (_error) {
      return text;
    }
  }

  if (data instanceof ArrayBuffer) {
    const decoder = new TextDecoder();
    const text = decoder.decode(data);

    if (!text) {
      return null;
    }

    try {
      return JSON.parse(text);
    } catch (_error) {
      return text;
    }
  }

  return data;
};

const extractFilename = (contentDisposition, fallback) => {
  if (!contentDisposition) {
    return fallback;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch (_error) {
      return utf8Match[1];
    }
  }

  const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] || fallback;
};

const extractErrorMessage = (data) => {
  if (!data) {
    return DEFAULT_DOWNLOAD_ERROR;
  }

  if (typeof data === "string") {
    return data;
  }

  if (Array.isArray(data)) {
    return data.filter(Boolean).join(" ") || DEFAULT_DOWNLOAD_ERROR;
  }

  if (typeof data === "object") {
    if (typeof data.detail === "string") {
      return data.detail;
    }

    if (Array.isArray(data.detail)) {
      return data.detail.filter(Boolean).join(" ") || DEFAULT_DOWNLOAD_ERROR;
    }

    if (typeof data.non_field_errors === "string") {
      return data.non_field_errors;
    }

    if (Array.isArray(data.non_field_errors)) {
      return (
        data.non_field_errors.filter(Boolean).join(" ") ||
        DEFAULT_DOWNLOAD_ERROR
      );
    }

    const firstValue = Object.values(data).find(Boolean);
    if (typeof firstValue === "string") {
      return firstValue;
    }
    if (Array.isArray(firstValue)) {
      return firstValue.filter(Boolean).join(" ") || DEFAULT_DOWNLOAD_ERROR;
    }
  }

  return DEFAULT_DOWNLOAD_ERROR;
};

const triggerDownload = (blob, filename) => {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => {
    window.URL.revokeObjectURL(blobUrl);
  }, 1000);
};

export const downloadInvoice = async (invoiceId) => {
  const response = await api.get(
    `${getBackendBaseUrl()}/invoices/${invoiceId}/download/`,
    {
      responseType: "blob",
    }
  );

  const fallbackFilename = `invoice_(${invoiceId}).pdf`;
  const filename = extractFilename(
    response.headers?.["content-disposition"],
    fallbackFilename
  );

  triggerDownload(response.data, filename);
};

export const getDownloadErrorMessage = async (error) => {
  const normalizedData = await normalizeResponseData(error?.response?.data);
  return extractErrorMessage(normalizedData);
};
